import React from 'react';
import { Button, Tooltip } from 'antd';
import { DeleteOutlined, EditOutlined, RightOutlined, WarningFilled } from '@ant-design/icons';
import JsonPanel from '@src/components/Common/JsonPanel';
import CollapsePanel from '@src/components/Common/CollapsePanel';
import EvaluationStatusTag from './EvaluationStatusTag';
import { EvaluationTableRow, rowConflict, rowStatus } from './rows';
import { declarationPreview, verdictLabel, verdictTone } from './utils';

/**
 * One line of the table: a case and its last verdict.
 *
 * Everything here comes off the row it was handed — nothing is looked up by
 * position in the case list, and `actual` being null (still queued, or an
 * answer that was not a verdict) is shown as no verdict rather than as a
 * rejection.
 */
const EvaluationRow = ({
    row,
    /** the run's columns are only there once there is a run to fill them */
    hasRun,
    open,
    toggle,
    readOnly,
    onEdit,
    onDelete,
    errors = [],
}: {
    row: EvaluationTableRow;
    hasRun: boolean;
    open: boolean;
    toggle: () => void;
    readOnly: boolean;
    onEdit: (index: number) => void;
    onDelete: (index: number) => void;
    /** what the API said about this case the last time it was sent */
    errors?: string[];
}) => {
    const status = rowStatus(row);
    const conflict = rowConflict(row);
    const number = row.caseIndex === null ? '—' : String(row.caseIndex + 1);
    const name = row.caseIndex === null ? 'Deleted case' : `Case ${row.caseIndex + 1}`;

    const description = hasRun
        ? `${name}, expected ${verdictLabel(row.expected)}, model answered ${verdictLabel(
              row.result?.actual ?? null,
          )}, ${status.text}`
        : `${name}, expected ${verdictLabel(row.expected)}`;

    return (
        <li className={`aip-row${open ? ' aip-row--open' : ''}${errors.length ? ' aip-row--bad' : ''}`}>
            <div
                className="aip-row__head"
                role="button"
                tabIndex={0}
                aria-expanded={open}
                aria-label={description}
                onClick={toggle}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggle();
                    }
                }}
            >
                <span className="aip-cell aip-cell--num">{number}</span>

                <span className="aip-cell aip-cell--decl">
                    <code className="aip-decl" title={declarationPreview(row.declaration, 400)}>
                        {declarationPreview(row.declaration)}
                    </code>
                    {conflict && (
                        <span className="aip-conflict">
                            <WarningFilled aria-hidden="true" />
                            {conflict}
                        </span>
                    )}
                </span>

                <span className="aip-cell" data-label="Expected">
                    <span className={`aip-verdict aip-verdict--${verdictTone(row.expected)}`}>
                        {verdictLabel(row.expected)}
                    </span>
                </span>

                {hasRun && (
                    <>
                        <span className="aip-cell" data-label="Model said">
                            <span className={`aip-verdict aip-verdict--${verdictTone(row.result?.actual ?? null)}`}>
                                {verdictLabel(row.result?.actual ?? null)}
                            </span>
                        </span>

                        <span className="aip-cell" data-label="Status">
                            <EvaluationStatusTag status={status} />
                        </span>
                    </>
                )}

                {/* these act on the case in place, so the click stops here
                    rather than opening the row underneath */}
                <span
                    className="aip-cell aip-cell--acts"
                    role="presentation"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                >
                    {!readOnly && row.caseIndex !== null && (
                        <>
                            <Tooltip title="Edit case">
                                <Button
                                    size="small"
                                    shape="circle"
                                    htmlType="button"
                                    aria-label={`Edit case ${row.caseIndex + 1}`}
                                    icon={<EditOutlined />}
                                    onClick={() => onEdit(row.caseIndex as number)}
                                />
                            </Tooltip>
                            <Tooltip title="Delete case">
                                <Button
                                    size="small"
                                    shape="circle"
                                    danger
                                    htmlType="button"
                                    aria-label={`Delete case ${row.caseIndex + 1}`}
                                    icon={<DeleteOutlined />}
                                    onClick={() => onDelete(row.caseIndex as number)}
                                />
                            </Tooltip>
                        </>
                    )}
                </span>

                <RightOutlined className="aip-row__chev" aria-hidden="true" />
            </div>

            <CollapsePanel open={open} className="aip-row__body">
                {errors.length > 0 && (
                    <ul className="aip-errors" role="alert">
                        {errors.map((message) => (
                            <li key={message}>{message}</li>
                        ))}
                    </ul>
                )}

                {row.result?.error && (
                    <p className="aip-row__error" role="alert">
                        {row.result.error}
                    </p>
                )}

                {/* the model's one-line verdict, which is what the row would
                    have carried if there were a column left to carry it in */}
                {row.result?.short_log && <p className="aip-row__summary">{row.result.short_log}</p>}

                <JsonPanel data={row.declaration} />

                {row.result?.detailed_log && (
                    <>
                        <h4 className="aip-row__h">Model reasoning</h4>
                        <pre className="aip-row__log">{row.result.detailed_log}</pre>
                    </>
                )}
            </CollapsePanel>
        </li>
    );
};

export default EvaluationRow;
